/**
 * Addon Configurator — Custom element that manages add-on product selections,
 * calculates running price totals, and intercepts cart submission to add
 * multiple items (main product + selected add-ons) in a single atomic call.
 *
 * Resolves addon variant IDs client-side via /products/{handle}.js when
 * server-side Liquid lookup (all_products) fails to populate them.
 */

if (!customElements.get('addon-configurator')) {
  class AddonConfigurator extends HTMLElement {
    constructor() {
      super();
      this.selections = new Map();
      this.variantCache = new Map(); // Cache for resolved variant IDs
      this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
      this.productHandle = this.dataset.productHandle;
      this._submitting = false; // Prevent double submission

      this.onCartRequest = this.onCartRequest.bind(this);
    }

    connectedCallback() {
      this.bindEvents();
      this.initSelections();
      this.resolveAllVariantIds();
      this.emitStateChange();
    }

    disconnectedCallback() {
      document.removeEventListener('product-form:cart:request', this.onCartRequest);
      const hotReload = this.closest('product-hot-reload');
      if (hotReload && this.variantObserver) {
        this.variantObserver.disconnect();
      }
    }

    bindEvents() {
      // Radio changes (covers select_one, toggle, and text_input opt-out)
      this.addEventListener('change', (e) => {
        const input = e.target;
        if (input.classList.contains('addon-cfg__input')) {
          this.handleOptionChange(input);
        }
      });

      // Text input changes
      this.addEventListener('input', (e) => {
        const input = e.target;
        if (input.classList.contains('addon-cfg__text-input')) {
          this.handleTextChange(input);
        }
      });

      // Auto-select text input radio when user focuses/types in text field
      this.addEventListener('focus', (e) => {
        const input = e.target;
        if (input.classList.contains('addon-cfg__text-input')) {
          const card = input.closest('.addon-cfg__card--text');
          if (card) {
            const textRadio = card.querySelector('.addon-cfg__input[value="text_input"]');
            if (textRadio && !textRadio.checked) {
              textRadio.checked = true;
              this.handleOptionChange(textRadio);
            }
          }
        }
      }, true);

      // Tap on text card selects the radio and enables/focuses the text input
      // (needed because the text input starts disabled when opt-out is selected)
      this.addEventListener('click', (e) => {
        const card = e.target.closest('.addon-cfg__card--text');
        if (!card) return;
        const textRadio = card.querySelector('.addon-cfg__input[value="text_input"]');
        const textInput = card.querySelector('.addon-cfg__text-input');
        if (textRadio && !textRadio.checked) {
          textRadio.checked = true;
          this.handleOptionChange(textRadio);
        }
        if (textInput && !textInput.disabled) {
          textInput.focus();
        }
      });

      // Cart submission interception
      document.addEventListener('product-form:cart:request', this.onCartRequest);

      // Listen for variant changes via hot-reload to update base price
      const hotReload = this.closest('product-hot-reload');
      if (hotReload) {
        this.variantObserver = new MutationObserver(() => {
          this.onHotReloadUpdate();
        });
        this.variantObserver.observe(hotReload, { attributes: true, attributeFilter: ['data-variant-id'] });
      }
    }

    /**
     * Resolve variant IDs for all addon inputs that have a handle but no variant ID.
     * Uses Shopify's /products/{handle}.js AJAX endpoint as a fallback when
     * the server-side all_products[] Liquid lookup returns nil.
     */
    async resolveAllVariantIds() {
      const inputs = this.querySelectorAll('.addon-cfg__input[type="radio"]');
      const handles = new Set();

      inputs.forEach((input) => {
        const handle = input.value;
        const existingId = input.dataset.variantId;
        if (handle && handle !== 'text_input') {
          if (!existingId) {
            handles.add(handle);
          }
        }
      });

      // Also check text_input fee handles
      this.querySelectorAll('.addon-cfg__text-group').forEach((group) => {
        const handle = group.dataset.feeHandle;
        if (handle && !group.dataset.feeVariantId) {
          handles.add(handle);
        }
      });

      if (handles.size === 0) return;

      // Resolve all missing handles in parallel
      const promises = [];
      for (const handle of handles) {
        promises.push(this.resolveVariantId(handle));
      }
      await Promise.allSettled(promises);

      // Patch DOM elements with resolved IDs
      inputs.forEach((input) => {
        const handle = input.value;
        if (handle && handle !== 'text_input' && !input.dataset.variantId) {
          const cached = this.variantCache.get(handle);
          if (cached) {
            input.dataset.variantId = cached.variantId;
            if (!parseInt(input.dataset.price, 10)) {
              input.dataset.price = cached.price;
            }
          }
        }
      });

      this.querySelectorAll('.addon-cfg__text-group').forEach((group) => {
        const handle = group.dataset.feeHandle;
        if (handle && !group.dataset.feeVariantId) {
          const cached = this.variantCache.get(handle);
          if (cached) {
            group.dataset.feeVariantId = cached.variantId;
            if (!parseInt(group.dataset.feePrice, 10)) {
              group.dataset.feePrice = cached.price;
            }
          }
        }
      });
    }

    /**
     * Fetch product data from Shopify AJAX API and cache the variant ID.
     */
    async resolveVariantId(handle) {
      if (this.variantCache.has(handle)) return this.variantCache.get(handle);

      try {
        const url = `${window.Shopify.routes.root}products/${handle}.js`;
        const response = await fetch(url);
        if (!response.ok) {
          console.warn('[AddonCfg] Product not found:', handle, '— status', response.status);
          return null;
        }

        const product = await response.json();
        const variant = product.variants.find((v) => v.available) || product.variants[0];
        if (variant) {
          const data = { variantId: String(variant.id), price: variant.price };
          this.variantCache.set(handle, data);
          return data;
        }
      } catch (e) {
        console.warn('[AddonCfg] Error resolving variant for:', handle, e);
      }
      return null;
    }

    /**
     * Read initial checked/selected states from the DOM
     */
    initSelections() {
      this.querySelectorAll('.addon-cfg__input[type="radio"]:checked').forEach((radio) => {
        const key = radio.dataset.groupKey;
        const variantId = radio.dataset.variantId;
        const price = parseInt(radio.dataset.price, 10) || 0;
        if (radio.value && radio.value !== 'text_input') {
          this.selections.set(key, {
            type: 'select_one',
            variantId,
            price,
            label: radio.dataset.label,
            groupLabel: radio.dataset.groupLabel,
            handle: radio.value
          });
        }
      });
    }

    handleOptionChange(input) {
      const key = input.dataset.groupKey;
      const price = parseInt(input.dataset.price, 10) || 0;

      if (!input.value) {
        // "No thank you" — remove selection
        this.selections.delete(key);
      } else if (input.value === 'text_input') {
        // Text input radio selected — selection handled by handleTextChange
      } else {
        // Read variant ID fresh from DOM (may have been patched by resolveAllVariantIds)
        const variantId = input.dataset.variantId || '';
        this.selections.set(key, {
          type: 'select_one',
          variantId,
          price,
          label: input.dataset.label,
          groupLabel: input.dataset.groupLabel,
          handle: input.value
        });
      }

      // Handle text input group opt-out toggle
      const group = input.closest('.addon-cfg__group');
      if (group) {
        const textGroup = group.querySelector('.addon-cfg__text-group');
        if (textGroup) {
          const textInput = textGroup.querySelector('.addon-cfg__text-input');
          if (textInput) {
            const isTextSelected = input.value === 'text_input';
            textInput.disabled = !isTextSelected;
            if (!isTextSelected) {
              textInput.value = '';
              this.selections.delete(key);
              const counter = textInput.closest('.addon-cfg__text-wrapper')?.querySelector('.addon-cfg__char-current');
              if (counter) counter.textContent = '0';
            }
          }
        }
      }

      this.emitStateChange();
    }

    handleTextChange(input) {
      const text = input.value.trim();
      const key = input.dataset.groupKey;
      const groupEl = input.closest('.addon-cfg__text-group');

      const counter = input.closest('.addon-cfg__text-wrapper')?.querySelector('.addon-cfg__char-current');
      if (counter) {
        counter.textContent = input.value.length;
      }

      if (!groupEl) return;

      const feeVariantId = groupEl.dataset.feeVariantId;
      const feePrice = parseInt(groupEl.dataset.feePrice, 10) || 0;

      if (text.length > 0) {
        this.selections.set(key, {
          type: 'text_input',
          variantId: feeVariantId,
          price: feePrice,
          label: text,
          groupLabel: input.dataset.groupLabel,
          handle: groupEl.dataset.feeHandle,
          text
        });
      } else {
        this.selections.delete(key);
      }

      this.emitStateChange();
    }

    onHotReloadUpdate() {
      const priceEl = this.closest('product-hot-reload')?.querySelector('[data-product-price]');
      if (priceEl) {
        const newPrice = parseInt(priceEl.dataset.productPrice, 10);
        if (!isNaN(newPrice)) {
          this.basePrice = newPrice;
          this.dataset.basePrice = newPrice;
          this.emitStateChange();
        }
      }
    }

    get totalAddonPrice() {
      let total = 0;
      for (const sel of this.selections.values()) {
        total += sel.price;
      }
      return total;
    }

    get grandTotal() {
      return this.basePrice + this.totalAddonPrice;
    }

    emitStateChange() {
      const detail = {
        selections: Object.fromEntries(this.selections),
        totalAddonPrice: this.totalAddonPrice,
        basePrice: this.basePrice,
        grandTotal: this.grandTotal
      };

      document.dispatchEvent(
        new CustomEvent('configurator:state-change', { bubbles: true, detail })
      );
    }

    /**
     * Intercept the product form's cart request to build a multi-item payload.
     * Always intercepts to ensure clean properties (no product-id/section-id).
     */
    async onCartRequest(event) {
      event.preventDefault();

      // Prevent double submission
      if (this._submitting) {
        console.warn('[AddonCfg] Ignoring duplicate cart request — already submitting');
        return;
      }
      this._submitting = true;

      const formData = event.detail.form;
      const mainVariantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity'), 10) || 1;

      try {
        const items = await this.buildCartItems(mainVariantId, quantity, formData);

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });

        const responseData = await response.json();

        if (!response.ok) {
          throw responseData;
        }

        event.detail.resolve(responseData);
      } catch (error) {
        console.error('[AddonCfg] Cart add failed:', error);
        event.detail.reject(error);
      } finally {
        this._submitting = false;
      }
    }

    /**
     * Re-read the current variant ID for an addon from the DOM.
     * The DOM may have been patched by resolveAllVariantIds after the user clicked.
     */
    _getCurrentVariantId(groupKey, handle) {
      const input = this.querySelector(
        `.addon-cfg__input[data-group-key="${groupKey}"][value="${handle}"]:checked`
      );
      if (input && input.dataset.variantId) {
        return input.dataset.variantId;
      }
      return '';
    }

    /**
     * Build the items[] array for the cart/add.js call.
     * Resolves missing variant IDs via AJAX before building the payload.
     */
    async buildCartItems(mainVariantId, quantity, formData) {
      const mainId = parseInt(mainVariantId, 10);
      const mainItem = {
        id: mainId,
        quantity
      };

      // Whitelist: only collect Shopify line item properties (properties[Name])
      const internalProps = new Set(['product-id', 'section-id']);
      const properties = {};
      for (const [key, value] of formData.entries()) {
        if (key.startsWith('properties[') && value) {
          const propName = key.replace(/^properties\[/, '').replace(/\]$/, '');
          if (!propName.startsWith('_') && !internalProps.has(propName)) {
            properties[propName] = value;
          }
        }
      }

      // Add engraving text as a line item property on the main product
      for (const [key, sel] of this.selections.entries()) {
        if (sel.type === 'text_input' && sel.text) {
          properties[sel.groupLabel] = sel.text;
        }
      }

      if (Object.keys(properties).length > 0) {
        mainItem.properties = properties;
      }

      const items = [mainItem];
      const unresolvedAddons = [];

      // Add-on product items — resolve missing variant IDs on-the-fly
      for (const [key, sel] of this.selections.entries()) {
        // Re-read variant ID from DOM in case it was patched after user clicked
        let variantId = sel.variantId || this._getCurrentVariantId(key, sel.handle);

        // If still missing, try AJAX fallback
        if (!variantId && sel.handle) {
          const resolved = await this.resolveVariantId(sel.handle);
          if (resolved) {
            variantId = resolved.variantId;
          }
        }

        if (!variantId || isNaN(parseInt(variantId, 10))) {
          console.warn('[AddonCfg] Could not resolve variant for addon:', key, 'handle:', sel.handle, 'variantId:', variantId);
          unresolvedAddons.push(sel.label || sel.groupLabel || key);
          continue;
        }

        const addonId = parseInt(variantId, 10);

        // Safety: skip addon if it resolved to the same variant as the main product
        if (addonId === mainId) {
          console.warn('[AddonCfg] Addon', key, 'resolved to SAME variant as main product (', mainId, ') — skipping to prevent duplicates. Handle:', sel.handle);
          unresolvedAddons.push(sel.label || sel.groupLabel || key);
          continue;
        }

        const addonItem = {
          id: addonId,
          quantity
        };

        addonItem.properties = {
          _parent_product: this.productHandle,
          _addon_type: key
        };

        items.push(addonItem);
      }

      // Warn if addons couldn't be resolved
      if (unresolvedAddons.length > 0) {
        console.warn('[AddonCfg] Unresolved addons (not added to cart):', unresolvedAddons.join(', '));
      }

      return items;
    }
  }

  customElements.define('addon-configurator', AddonConfigurator);
}
